import {
  AssistanceBookPayload, AssistanceReservationInfo, AssistanceSearchPayload
} from '@shared/travel-assistance/entities/booking-assistance';
import { Travelers } from '@booking/entities/booking-arrangement';
import { formatStrinWithTimeZoneDateByRegex } from '@ux/mappers/date.mapper';
import { travelerAgeInYears } from '@pax/mappers/traveler-age.mapper';
import { User } from '@app/modules/core/interfaces/user';
import { languageFormat } from '@ux/mappers/language.mapper';
import { environment as ENV } from '@environment';
import { SearchBarState } from '@shared/base-search-bar/store/state/search-bar.state';
import {
  DetailReservation, ReservationFlight, ReservationHotel, ReservationVehicle
} from '@user-history/entities/history-booking-detail';
import { ReservationType } from '@user-history/entities/user-history-item';
import { UserGender } from '@booking/entities/user-gender';
import { getRouteType } from '@app/modules/booking/mappers/assistance-search-payload.mapper';
import { StatsigExperimentsConfig } from '@statsig/statsig.entities';
import { customParamsMapper } from '@app/mappers/custom-params.mapper';

export function getAssistanceReservationSearchPayloadByCrossSelling(
  crossSellingType: ReservationType, reservation: DetailReservation, lastReservation?: DetailReservation
): AssistanceReservationInfo {
  if (crossSellingType === ReservationType.Assistance) {
    switch (reservation?.ReservationType) {
      case ReservationType.Flight:
      case ReservationType.Bus:
      case ReservationType.AirPackage:
        return assistanceInfoByAiReservation(reservation as ReservationFlight, lastReservation as ReservationFlight);
      case ReservationType.Hotel:
        return assistanceInfoByHotelReservation(reservation as ReservationHotel);
      case ReservationType.Auto:
        return assistanceInfoByVehicleReservation(reservation as ReservationVehicle);
      default:
        return null;
    }
  } else return null;
}

export function assistanceReservationPayloadByCrossSelling(
  info: AssistanceReservationInfo,
  currencyCode: string,
  searchData: SearchBarState,
  user: User,
  type: ReservationType,
  numSectors: number,
  userExperiments: StatsigExperimentsConfig[]
): AssistanceSearchPayload {
  let infoItem = {};
  switch (type) {
    case ReservationType.Flight:
      infoItem = {
        Air_AssistanceReservation: {
          TravelItineraryId: info.TravelItineraryId,
          EndDate: info.EndDate,
          RouteType: getRouteType(numSectors)
        }
      };
      break;
    default:
      infoItem = { AssistanceReservation: info };
      break;
  }
  return {
    ...infoItem,
    RequestSettings: {
      SalesChanelCode: searchData.salesChannel,
      BusinessUnitCode: ENV.business_unit_code,
      BranchCode: searchData.branchCode,
      Language: languageFormat(ENV.countryInfo.locale),
      PromoCode: user.promo_code || null,
      CustomParams: customParamsMapper(user, userExperiments)
    },
    CurrencyCode: currencyCode,
    SessionToken: null,
  };
}

export function assistanceBookPayloadByCrossSelling(
  searchId: string, passengers: Travelers[], assistanceId: number, itineraryId: string
): AssistanceBookPayload {
  return {
    BookRequest: {
      OptionId: assistanceId,
      Passengers: passengers.map(pas => ({ ...pas, Gender: UserGender.Male })),
      TravelItineraryId: Number(itineraryId)
    },
    SearchId: searchId,
    SessionToken: null,
  };
}

function assistanceInfoByAiReservation(
  reservation: ReservationFlight, lastReservation?: ReservationFlight
): AssistanceReservationInfo {
  const allSectors = reservation?.Sectors?.length;
  const lastReservationSectors = lastReservation?.Sectors?.length;
  if (allSectors) {
    const sectors = reservation.Sectors;
    const departure = sectors[0];
    const arrival = lastReservationSectors ? lastReservation.Sectors[lastReservationSectors - 1]  : sectors[allSectors - 1];
    const departureFlightRef = departure.Flights[0];
    const arrivalByDepartureFlightRef = departure.Flights[departure.Flights.length - 1];
    const departureByArrivalFlightRef = arrival.Flights[arrival.Flights.length - 1];
    const reservationDate = departureFlightRef?.DepartureDate ?
      new Date(formatStrinWithTimeZoneDateByRegex(departureFlightRef.DepartureDate)) : new Date();
    return {
      OriginCityCode: departureFlightRef.DepartureAirport,
      DestinationCityCode: arrivalByDepartureFlightRef.ArrivalAirport,
      StartDate: departureFlightRef.DepartureDate,
      EndDate: departureByArrivalFlightRef.ArrivalDate,
      PassengerAges: reservation?.Passengers.map(traveler => {
        const age = travelerAgeInYears(traveler.DOB, reservationDate);
        return age ? age : 1;
      }),
      TravelItineraryId: reservation.TravelItineraryId,
    };
  } else return null;
}

function assistanceInfoByHotelReservation(reservation: ReservationHotel): AssistanceReservationInfo {
  if (reservation) {
    const reservationDate = reservation?.Checkin ?
      new Date(formatStrinWithTimeZoneDateByRegex(reservation.Checkin)) : new Date();
    return {
      OriginCityCode: reservation.Hotel?.CityCode,
      DestinationCityCode: reservation.Hotel?.CityCode,
      StartDate: reservation.Checkin,
      EndDate: reservation.Checkout,
      PassengerAges: reservation?.Passengers.map(traveler => {
        const age = travelerAgeInYears(traveler.DOB, reservationDate);
        return age ? age : 1;
      }),
      TravelItineraryId: reservation.TravelItineraryId,
    };
  } else return null;
}

function assistanceInfoByVehicleReservation(reservation: ReservationVehicle): AssistanceReservationInfo {
  if (reservation) {
    const reservationDate = reservation?.PickupDateTime ?
      new Date(formatStrinWithTimeZoneDateByRegex(reservation.PickupDateTime)) : new Date();
    return {
      OriginCityCode: reservation.PickupLocation?.DestinationCode,
      DestinationCityCode: reservation.DropOffLocation?.DestinationCode,
      StartDate: reservation.PickupDateTime,
      EndDate: reservation.DropOffDateTime,
      PassengerAges: reservation?.Passengers.map(traveler => {
        const age = travelerAgeInYears(traveler.DOB, reservationDate);
        return age ? age : 1;
      }),
      TravelItineraryId: reservation.TravelItineraryId,
    };
  } else return null;
}

export const exportedForTesting = {
  assistanceInfoByAiReservation
}
